/**
 * ReaderJourneyBreakdown.jsx — June 2 2026.
 *
 * The reader-side twin of WriterJourneyBreakdown — a single page with
 * three tabs that let the reader inspect exactly how their Streak, Total
 * XP, and Level numbers were computed. Reached by tapping any of the
 * three hero cards on the dashboard.
 *
 * `?type=streak|xp|level` picks the initial tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Flame, Sparkles, Crown, BookOpen, Headphones, Film,
  Loader2, CheckCircle2, Lock,
} from 'lucide-react';
import { authFetch, checkAuth } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import { toast } from 'sonner';
import JourneyRoadmap from '../components/JourneyRoadmap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtMins = (totalSeconds) => {
  const s = Number(totalSeconds || 0);
  if (s <= 0) return '';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};
const fmtDay = (iso) => {
  // "2026-06-02" → "Tue 2"
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const TYPE_ICON = {
  ebook:     BookOpen,
  audiobook: Headphones,
  cinematic: Film,
};

const ReaderJourneyBreakdown = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Accept both legacy `?type=` and the new `?focus=` (Jun 2 2026 user
  // spec — reader-stats tiles deep-link via `focus`). `speed` collapses
  // into the XP tab since reading-speed is a derived sub-stat there.
  const incomingFocus = params.get('focus') || params.get('type') || '';
  const resolved =
    incomingFocus === 'streak' || incomingFocus === 'level' ? incomingFocus :
    incomingFocus === 'speed' ? 'xp' :
    'xp';
  const [tab, setTab] = useState(resolved);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evolving, setEvolving] = useState(false);

  const fetchAll = async () => {
    const r = await authFetch(`${BACKEND_URL}/api/reader/breakdown`);
    const d = r.ok ? await r.json() : null;
    setData(d);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      await fetchAll();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleEvolve = async () => {
    if (evolving) return;
    setEvolving(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/reader/evolve`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Evolution failed.');
        return;
      }
      toast.success(`Evolved to Reader Lv ${d.newLevel}!`);
      await fetchAll();
    } catch {
      toast.error('Network error during evolution.');
    } finally {
      setEvolving(false);
    }
  };

  if (loading || !data) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-slate-400 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading breakdown…
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="reader-breakdown-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-sm"
          data-testid="reader-breakdown-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
          Reader Journey
        </span>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <TabBtn id="xp"     label="Total XP" Icon={Sparkles} active={tab === 'xp'}     onClick={() => setTab('xp')} />
        <TabBtn id="streak" label="Streak"   Icon={Flame}    active={tab === 'streak'} onClick={() => setTab('streak')} />
        <TabBtn id="level"  label="Level"    Icon={Crown}    active={tab === 'level'}  onClick={() => setTab('level')} />
      </div>

      {tab === 'xp'     && <XPSection xp={data.xp} />}
      {tab === 'streak' && <StreakSection streak={data.streak} />}
      {tab === 'level'  && (
        <>
          <LevelSection
            level={data.level}
            onEvolve={handleEvolve}
            evolving={evolving}
          />
          {/* Roadmap to The Voracious — the full tier-by-tier climb shown
              below the level ladder so a reader can see exactly what
              they need to do to climb. */}
          <section className="mt-8">
            <JourneyRoadmap kind="reader" />
          </section>
        </>
      )}
    </main>
  );
};

const TabBtn = ({ id, label, Icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-bold transition-colors ${
      active
        ? 'bg-amber-600/25 text-amber-100 border-amber-400/40 ring-1 ring-amber-400/40'
        : 'bg-slate-800/50 text-slate-300 border-slate-700 hover:bg-slate-800'
    }`}
    data-testid={`reader-breakdown-tab-${id}`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

// ─────────────────────────────────────────────────────── XP
const XPSection = ({ xp }) => {
  const books = xp.books || [];
  return (
    <section data-testid="reader-breakdown-xp">
      <Hero
        label="Total XP earned"
        value={fmtNum(xp.total)}
        suffix="XP"
        tint="from-amber-500/25 via-orange-500/15 to-fuchsia-500/15 border-amber-400/40"
        Icon={Sparkles}
        caption="Sum of every XP event credited to your reader account."
        testid="reader-xp-hero"
      />
      <h2 className="text-sm font-bold text-slate-300 mt-5 mb-2">By source</h2>
      <div className="space-y-2 mb-5" data-testid="reader-xp-sources">
        {xp.sources?.map((s) => (
          <div
            key={s.source}
            className="flex items-center justify-between p-3 rounded-xl bg-slate-800/60 border border-slate-700"
            data-testid={`reader-xp-source-${s.source}`}
          >
            <span className="text-sm text-slate-200 capitalize">{s.source.replace(/_/g, ' ')}</span>
            <span className="text-sm font-black text-white tabular-nums">{fmtNum(s.xp)} XP</span>
          </div>
        ))}
        {(!xp.sources || xp.sources.length === 0) && (
          <p className="text-xs text-slate-500 py-3 text-center">No XP events yet.</p>
        )}
      </div>

      <h2 className="text-sm font-bold text-slate-300 mt-5 mb-2">By book</h2>
      <p className="text-[11px] text-slate-500 mb-2">
        Each book/episode contributes ~1 XP per 100 words read and ~1 XP per audio minute listened.
      </p>
      <div className="space-y-2" data-testid="reader-xp-books">
        {books.map((b) => {
          const kind = b.type === 'ebook' ? 'ebook'
            : b.type === 'cinematic' ? 'cinematic'
            : 'audiobook';
          const Icon = TYPE_ICON[kind] || BookOpen;
          const cover = b.cover_image ? resolveCoverUrl(b.cover_image) : null;
          return (
            <div
              key={b.book_id}
              className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-800/60 border border-slate-700"
              data-testid={`reader-xp-book-${b.book_id}`}
            >
              <div className="w-10 h-10 rounded-lg bg-slate-900/60 border border-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <Icon className="w-5 h-5 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{b.title}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {b.word_count ? `${fmtNum(b.word_count)} words` : null}
                  {b.word_count && b.duration_seconds ? ' · ' : ''}
                  {b.duration_seconds ? fmtMins(b.duration_seconds) : null}
                  {b.author ? ` · by ${b.author}` : ''}
                </p>
              </div>
              <div className="text-right tabular-nums flex-shrink-0">
                <p className="text-white font-black text-base leading-none">
                  +{fmtNum(b.xp_earned)} <span className="text-amber-300 text-xs">XP</span>
                </p>
                {b.xp_estimated && (
                  <p className="text-[10px] text-slate-500 mt-1">estimate</p>
                )}
              </div>
            </div>
          );
        })}
        {books.length === 0 && (
          <p className="text-xs text-slate-500 py-6 text-center">
            No completed books yet. Finish a book or audiobook to see it here.
          </p>
        )}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────── Streak
const StreakSection = ({ streak }) => {
  const days = streak.days || [];
  const counted = days.filter((d) => d.active);
  return (
    <section data-testid="reader-breakdown-streak">
      <Hero
        label="Current streak"
        value={fmtNum(streak.current)}
        suffix={`day${streak.current === 1 ? '' : 's'}`}
        tint="from-rose-500/25 via-orange-500/15 to-amber-500/15 border-rose-400/40"
        Icon={Flame}
        caption={
          streak.last_active_date
            ? `Last active ${streak.last_active_date}. Best ever: ${streak.best} days.`
            : `Best ever: ${streak.best} days.`
        }
        testid="reader-streak-hero"
      />

      <h2 className="text-sm font-bold text-slate-300 mt-5 mb-2">Last 14 days</h2>
      <div className="grid grid-cols-7 gap-2" data-testid="reader-streak-grid">
        {days.map((d) => (
          <div
            key={d.date}
            className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-center p-1 ${
              d.active
                ? 'bg-rose-500/20 border-rose-400/40 text-rose-100'
                : d.is_today
                ? 'bg-slate-800/60 border-amber-400/40 text-slate-200'
                : 'bg-slate-900/40 border-slate-700 text-slate-500'
            }`}
            data-testid={`reader-streak-day-${d.date}`}
            title={d.date}
          >
            <Flame className={`w-3.5 h-3.5 ${d.active ? 'text-rose-300' : 'text-slate-600'}`} />
            <span className="text-[10px] font-bold mt-0.5">{fmtDay(d.date)}</span>
            {d.is_today && <span className="text-[8px] uppercase tracking-wider mt-0.5">today</span>}
          </div>
        ))}
      </div>

      <p className="text-[11px] text-slate-500 mt-4">
        A day counts when you complete a lesson, read a book, listen to an episode, or earn any XP.
        Activities counted: <span className="text-rose-300 font-bold">{counted.length}</span> of the last 14 days.
      </p>
    </section>
  );
};

// ─────────────────────────────────────────────────────── Level
const LevelSection = ({ level, onEvolve, evolving }) => {
  // Render the next 30 levels around the current level so the page stays
  // useful without dumping all 100 rows at once.
  const visible = useMemo(() => {
    const ladder = level.ladder || [];
    const idx = ladder.findIndex((l) => l.current);
    const start = Math.max(0, idx - 2);
    return ladder.slice(start, start + 30);
  }, [level.ladder]);

  const percent = Math.min(100, Math.round((level.xp_in_current_level / Math.max(1, level.xp_for_next_level)) * 100));

  return (
    <section data-testid="reader-breakdown-level">
      <Hero
        label={`Reader Lv ${level.current}`}
        value={level.tier}
        suffix=""
        tint="from-emerald-500/20 via-sky-500/15 to-fuchsia-500/15 border-emerald-400/40"
        Icon={Crown}
        caption={`${fmtNum(level.xp_in_current_level)} / ${fmtNum(level.xp_for_next_level)} banked · ${fmtNum(level.total_xp)} lifetime XP`}
        testid="reader-level-hero"
      />

      {/* Progress bar */}
      <div className="mt-3">
        <div className="w-full bg-slate-800 rounded-full h-2.5">
          <div
            className="h-2.5 rounded-full transition-all"
            style={{
              width: `${percent}%`,
              background: `linear-gradient(90deg, ${level.tier_hex}aa, ${level.tier_hex})`,
            }}
          />
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">{percent}% of the way to Lv {level.current + 1}</p>
      </div>

      {/* Evolve CTA */}
      {level.evolution_eligible && (
        <button
          type="button"
          onClick={onEvolve}
          disabled={evolving}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 font-bold text-white animate-pulse-evolve focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:opacity-60"
          style={{ background: `linear-gradient(90deg, ${level.tier_hex}, #f59e0b)` }}
          data-testid="reader-evolve-btn"
        >
          {evolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {evolving ? 'Evolving…' : `Evolve to Lv ${level.current + 1}`}
        </button>
      )}

      <h2 className="text-sm font-bold text-slate-300 mt-6 mb-2">The ladder</h2>
      <p className="text-[11px] text-slate-500 mb-3">Each level needs the XP shown. Tier titles change every 10 levels.</p>
      <div className="space-y-1.5" data-testid="reader-level-ladder">
        {visible.map((l) => {
          const isCurrent = l.current;
          const isPast = l.completed;
          return (
            <div
              key={l.level}
              className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                isCurrent
                  ? 'bg-amber-500/15 border-amber-400/50 ring-1 ring-amber-400/40'
                  : isPast
                  ? 'bg-slate-900/50 border-slate-700'
                  : 'bg-slate-800/40 border-slate-700'
              }`}
              data-testid={`reader-level-row-${l.level}`}
            >
              <span
                className="w-9 h-9 rounded-md flex items-center justify-center text-xs font-black flex-shrink-0"
                style={{ background: `${l.tier_hex}22`, color: l.tier_hex, border: `1px solid ${l.tier_hex}55` }}
              >
                {l.level}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${isCurrent ? 'text-white' : isPast ? 'text-slate-300' : 'text-slate-200'}`}>
                  Lv {l.level} · <span style={{ color: l.tier_hex }}>{l.tier}</span>
                </p>
                <p className="text-[11px] text-slate-500">
                  {fmtNum(l.xp_required)} XP this level · {fmtNum(l.xp_cumulative)} cumulative
                </p>
              </div>
              {isPast && <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
              {!isPast && !isCurrent && <Lock className="w-3 h-3 text-slate-500 flex-shrink-0" />}
              {isCurrent && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-200 bg-amber-500/20 border border-amber-400/40 rounded-full px-2 py-0.5">
                  current
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────── Hero (shared)
const Hero = ({ label, value, suffix, tint, Icon, caption, testid }) => (
  <section
    className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-5 ${tint}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4" />
      <span className="text-[11px] uppercase tracking-widest font-bold opacity-80">{label}</span>
    </div>
    <p className="text-3xl sm:text-4xl font-black text-white tabular-nums">
      {value}
      {suffix && <span className="text-amber-200 text-lg align-middle ml-1">{suffix}</span>}
    </p>
    {caption && <p className="text-[11px] text-white/70 mt-1">{caption}</p>}
  </section>
);

export default ReaderJourneyBreakdown;
